import React, { useState } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { subscriptionApi } from '../../services/api';
import Swal from 'sweetalert2';
import './SubscriptionModal.css';

const SubscriptionModal: React.FC = () => {
  const { isModalOpen, closeModal, subscription, refreshSubscription, daysRemaining } = useSubscription();
  const [activationCode, setActivationCode] = useState('');
  const [submittingCode, setSubmittingCode] = useState(false);
  const [requestingPlan, setRequestingPlan] = useState<string | null>(null);

  if (!isModalOpen) return null;

  const plans = subscription?.plans || [
    {
      id: 'monthly',
      name: 'Aylık Plan',
      price: 150,
      priceLabel: '150 ₺ / Ay',
      duration: '30 Gün',
      features: ['Sınırsız Manuel İşyeri Kaydı', 'Tüm İSG Form & Şablonları', 'Mobil PWA Desteği', 'Bulut Yedekleme']
    },
    {
      id: 'semiyearly',
      name: '6 Aylık Plan',
      price: 750,
      priceLabel: '750 ₺ (125 ₺/Ay)',
      duration: '180 Gün',
      popular: true,
      features: ['1 Ay Kazançlı', 'Sınırsız Manuel İşyeri Kaydı', 'Tüm İSG Form & Şablonları', 'Mobil PWA Desteği', 'Öncelikli Destek']
    },
    {
      id: 'yearly',
      name: 'Yıllık Plan',
      price: 1500,
      priceLabel: '1.500 ₺ (125 ₺/Ay)',
      duration: '365 Gün',
      features: ['2 Ay Ücretsiz!', 'Sınırsız Manuel İşyeri Kaydı', 'Tüm İSG Form & Şablonları', 'Mobil PWA Desteği', 'VIP Telegram Destek Hattı']
    }
  ];

  const handleRequestPlan = async (planId: string, planName: string) => {
    setRequestingPlan(planId);
    try {
      const res = await subscriptionApi.requestActivation(planId);
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Talebiniz Alındı!',
          text: `${planName} için aktivasyon talebiniz Telegram yöneticisine iletildi. Kodunuz hazırlandığında tarafınıza iletilecektir.`,
          confirmButtonColor: '#1e3c72'
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.response?.data?.message || 'Talep iletilirken bir hata oluştu.',
        confirmButtonColor: '#1e3c72'
      });
    } finally {
      setRequestingPlan(null);
    }
  };

  const handleApplyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activationCode.trim()) {
      Swal.fire('Uyarı', 'Lütfen aktivasyon kodunu giriniz.', 'warning');
      return;
    }

    setSubmittingCode(true);
    try {
      const res = await subscriptionApi.applyCode(activationCode);
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Aktivasyon Başarılı! 🎉',
          text: res.message,
          confirmButtonColor: '#10b981'
        });
        setActivationCode('');
        await refreshSubscription();
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Aktivasyon Başarısız',
        text: err.response?.data?.message || 'Kod uygulanamadı. Lütfen kontrol ediniz.',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setSubmittingCode(false);
    }
  };

  return (
    <div className="subscription-modal-backdrop" onClick={closeModal}>
      <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Kapat Butonu */}
        <button className="modal-close-btn" onClick={closeModal}>✕</button>

        {/* Modal Başlık */}
        <div className="modal-header-section">
          <span className="current-status-tag">
            {daysRemaining > 0 ? `Aktif Süreniz: ${daysRemaining} Gün Kaldı` : 'Süreniz Sona Erdi'}
          </span>
          <h2 className="modal-title">İSG Uzmanı Lisans Paketleri</h2>
          <p className="modal-subtitle">
            Tüm özelliklere kesintisiz erişmek için size uygun planı seçip Telegram botumuz üzerinden kod talep edebilirsiniz.
          </p>
        </div>

        {/* Fiyatlandırma Kartları */}
        <div className="plans-grid">
          {plans.map((p) => (
            <div key={p.id} className={`plan-card ${p.popular ? 'popular' : ''}`}>
              {p.popular && <div className="popular-badge">EN ÇOK TERCİH EDİLEN</div>}
              <h3 className="plan-name">{p.name}</h3>
              <div className="plan-price-block">
                <span className="plan-price">{p.priceLabel}</span>
                <span className="plan-duration">{p.duration} Kullanım</span>
              </div>
              <ul className="plan-features">
                {p.features.map((feat, idx) => (
                  <li key={idx}>
                    <span className="check-icon">✓</span> {feat}
                  </li>
                ))}
              </ul>
              <button
                className={`plan-action-btn ${p.popular ? 'primary' : 'secondary'}`}
                onClick={() => handleRequestPlan(p.id, p.name)}
                disabled={requestingPlan === p.id}
              >
                {requestingPlan === p.id ? 'İletiliyor...' : 'Talep Et (Telegram) 📱'}
              </button>
            </div>
          ))}
        </div>

        {/* Aktivasyon Kodu Girişi */}
        <div className="activation-code-card">
          <div className="code-card-header">
            <h4>🔑 Elinizde Aktivasyon Kodu Var mı?</h4>
            <p>Telegram botu veya yöneticinizden aldığınız kodu buraya girerek lisansınızı anında başlatın.</p>
          </div>
          <form onSubmit={handleApplyCode} className="code-input-form">
            <input
              type="text"
              placeholder="Örn: UZMAN-ABCD-1234"
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
              className="code-input"
              maxLength={24}
            />
            <button type="submit" className="code-submit-btn" disabled={submittingCode}>
              {submittingCode ? 'Kontrol Ediliyor...' : 'Kodu Doğrula & Başlat'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
